import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { havenColors } from '../../theme/theme';

// Images
import familyBackgroundImage from '../../assets/images/family-background.jpg';
import phoneImage from '../../assets/images/phone-installation-successful.png';
import patentedMonitoringImage from '../../assets/images/patented-monitoring.png';
import controllerAutomationImage from '../../assets/images/controller-automation.jpg';
import havenControllerImage from '../../assets/images/haven-controller-shadow.png';
import getStartedBackground from '../../assets/images/what-does-it-monitor.png';

const ActiveAirManagementPage: React.FC = () => {
  return (
    <Box>
      {/* Hero Section - What is Active Air Management */}
      <Box
        sx={{
          py: 8,
          background: `linear-gradient(rgba(0, 196, 193, 0.85), rgba(0, 196, 193, 0.85)), url(${familyBackgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={7}>
              <Typography variant="h2" sx={{ 
                fontSize: { xs: '2.5rem', md: '3rem' }, 
                fontWeight: 'bold', 
                mb: 4,
                lineHeight: 1.2
              }}>
                What is Active Air Management?
              </Typography>
              
              <Typography variant="h6" sx={{ 
                mb: 6, 
                lineHeight: 1.6,
                fontSize: '1.2rem',
                opacity: 0.95
              }}>
                Active Air Management is the proactive & ongoing care of your home's environment for optimal comfort, health, and safety; delivered by your trusted HVAC professional.
              </Typography>

              {/* Feature Points */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                  <CheckCircleIcon sx={{ 
                    color: 'white', 
                    fontSize: '1.5rem', 
                    mr: 2, 
                    mt: 0.5,
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                  }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: '1.1rem', 
                    lineHeight: 1.6 
                  }}>
                    <strong>Professional-grade sensors</strong> monitor your home's air to identify issues.
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                  <CheckCircleIcon sx={{ 
                    color: 'white', 
                    fontSize: '1.5rem', 
                    mr: 2, 
                    mt: 0.5,
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                  }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: '1.1rem', 
                    lineHeight: 1.6 
                  }}>
                    <strong>Equipment automations</strong> react to daily Indoor Air Quality (IAQ) events such as cooking and cleaning, to optimize your home's health and airflow.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right Content - Phone Mockup */}
            <Grid item xs={12} md={5}>
              <Box sx={{ 
                textAlign: 'center',
                position: 'relative'
              }}>
                <img 
                  src={phoneImage} 
                  alt="HAVEN Mobile App" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '300px',
                    height: 'auto'
              }} 
            />
          </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Product Line Section */}
      <Box sx={{ 
        minHeight: '150px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'white',
        textAlign: 'center'
      }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ 
            fontSize: { xs: '2rem', md: '2.5rem' }, 
            fontWeight: 'normal', 
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            fontFamily: 'Rubik, sans-serif',
            color: havenColors.darkGray
          }}>
            PRODUCT LINE
          </Typography>
        </Container>
      </Box>

      {/* Patented Monitoring Section */}
      <Box
        sx={{
          minHeight: '600px',
          display: 'flex',
          alignItems: 'center',
          background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${patentedMonitoringImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ 
            fontSize: { xs: '1.8rem', md: '2.8rem' }, 
            fontWeight: 'normal', 
            lineHeight: 1.3,
            maxWidth: '900px',
            mx: 'auto',
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
          }}>
            Patented, in-duct air quality monitoring, compatible with any forced air HVAC system.
          </Typography>
        </Container>
      </Box>

      {/* Controller Automation Section */}
            <Box
              sx={{
          minHeight: '600px',
                display: 'flex',
                alignItems: 'center',
          background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${controllerAutomationImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ 
            fontSize: { xs: '1.8rem', md: '2.8rem' }, 
            fontWeight: 'normal', 
            lineHeight: 1.3,
            maxWidth: '900px',
            mx: 'auto',
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
          }}>
            Full automation controls compatible with air handlers, ERV/HRVs, dehumidifiers & humidifiers.
          </Typography>
        </Container>
      </Box>

      {/* HAVEN Central Air Controller Section */}
      <Box sx={{ 
        py: 8, 
        backgroundColor: 'white'
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Controller Content */}
            <Grid item xs={12} md={8}>
              <Typography variant="h3" sx={{ 
                fontSize: { xs: '2rem', md: '2.5rem' }, 
                fontWeight: 'bold', 
                color: havenColors.primaryTeal,
                mb: 2
              }}>
                HAVEN Central Air Controller
              </Typography>
              
              <Typography variant="h6" sx={{ 
                mb: 4, 
                color: havenColors.darkGray,
                fontWeight: 'normal'
              }}>
                For homes with multiple automation requirements
        </Typography>
        
              {/* Feature List */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckCircleIcon sx={{ 
                    color: havenColors.primaryTeal, 
                    fontSize: '1.5rem', 
                    mr: 2, 
                    mt: 0.5
                  }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: '1.1rem', 
                    lineHeight: 1.6,
                    color: havenColors.darkGray
                  }}>
                    Custom ventilation scheduling
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckCircleIcon sx={{ 
                    color: havenColors.primaryTeal, 
                    fontSize: '1.5rem', 
                    mr: 2, 
                    mt: 0.5
                  }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: '1.1rem', 
                    lineHeight: 1.6,
                    color: havenColors.darkGray
                  }}>
                    Connect up to 2 pieces of HVAC equipment
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckCircleIcon sx={{ 
                    color: havenColors.primaryTeal, 
                    fontSize: '1.5rem', 
                    mr: 2, 
                    mt: 0.5
                  }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: '1.1rem', 
                    lineHeight: 1.6,
                    color: havenColors.darkGray
                  }}>
                    Provides smart controls for connected equipment
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckCircleIcon sx={{ 
                    color: havenColors.primaryTeal, 
                    fontSize: '1.5rem', 
                    mr: 2, 
                    mt: 0.5
                  }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: '1.1rem', 
                    lineHeight: 1.6,
                    color: havenColors.darkGray
                  }}>
                    Triggers equipment when air quality event is detected
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckCircleIcon sx={{ 
                    color: havenColors.primaryTeal, 
                    fontSize: '1.5rem', 
                    mr: 2, 
                    mt: 0.5
                  }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: '1.1rem', 
                    lineHeight: 1.6,
                    color: havenColors.darkGray
                  }}>
                    Ensures consistent airflow sampling for Central Air Monitor
                </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckCircleIcon sx={{ 
                    color: havenColors.primaryTeal, 
                    fontSize: '1.5rem', 
                    mr: 2, 
                    mt: 0.5
                  }} />
                  <Typography variant="body1" sx={{ 
                    fontSize: '1.1rem', 
                    lineHeight: 1.6,
                    color: havenColors.darkGray
                  }}>
                    Professional installation
                </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Controller Image */}
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center'
              }}>
                <img 
                  src={havenControllerImage} 
                  alt="HAVEN Central Air Controller" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '350px',
                    height: 'auto'
                  }} 
                />
              </Box>
            </Grid>
        </Grid>
      </Container>
      </Box>

      {/* Get Started Section */}
      <Box
        sx={{
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${getStartedBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
            <Button
              variant="contained"
              size="large"
            href="https://haveniaq.com/get-started/"
            target="_blank"
            rel="noopener noreferrer"
              sx={{
              backgroundColor: havenColors.primaryTeal,
                color: 'white',
              fontWeight: 'bold',
              px: 6,
              py: 2,
              fontSize: '1.2rem',
              borderRadius: '30px',
              textTransform: 'uppercase',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                '&:hover': {
                backgroundColor: havenColors.darkTeal,
                boxShadow: '0 6px 25px rgba(0,0,0,0.4)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
              }}
            >
            GET STARTED WITH HAVEN
            </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default ActiveAirManagementPage; 